/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { alias } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import SnapshotType from '@linc-technologies/linced-common/enums/snapshot-type';

export default class ReportSnapshotsController extends Controller {
  Jurisdiction = Jurisdiction;
  SnapshotType = SnapshotType;

  // Services
  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  students;

  // Attributes
  @tracked
  currentProcessMonitorId = '';

  finishedGeneration = true;
  pollingResults = [];
  @tracked
  displayedCustomSnapshotSets = [];
  @tracked
  displayedAutoSnapshotSets = [];
  showAutoSnapshots = false;

  // Auto-sets regeneration
  @tracked
  startDate = '';

  @tracked
  endDate = '';
  @tracked
  term = '';
  @tracked
  year = '';
  @tracked
  selectedType = '';

  // Computed:
  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('session.scopes.[]')
  get canRegenerate() {
    // Limit the overwrite functionality only to Hero Managers and higher
    return this.permissions.meet(Scopes.LINCED_MANAGER);
  }

  @computed('session.scopes.[]')
  get canGenerate() {
    // Limit generation functionality only to Tenant Admin and higher
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @computed('session.scopes.[]')
  get canViewLogs() {
    // Limit the logs visibility functionality only to Hero Managers and higher
    return this.permissions.meet(Scopes.LINCED_MANAGER);
  }

  // removedDuplicateAutoSnapshots filters out auto-generated sets of the selected type that have duplicate
  // date ranges and instead uses the newest one
  @computed(
    'displayedAutoSnapshotSets.@each.{snapshotType,startDate,endDate,createTime}',
  )
  get removedDuplicateAutoSnapshots() {
    if (isEmpty(this.displayedAutoSnapshotSets)) {
      return [];
    }
    let seen = new Map();

    this.displayedAutoSnapshotSets.forEach((set) => {
      let key = `${set.startDate}|${set.endDate}`;

      // Only replace if this one is newer
      if (!seen.has(key) || set.createTime > seen.get(key).createTime) {
        seen.set(key, set);
      }
    });

    return Array.from(seen.values());
  }

  // Hooks
  init() {
    super.init(...arguments);
    // Bind the context for passing the function to the generate-snapshot component
    this.refreshSnapshotSets = this.refreshSnapshotSets.bind(this);
  }

  // Functions
  async refreshSnapshotSets(mode) {
    if (mode === 'custom') {
      let newCustomSets = await this.store.query('snapshots/snapshot-set', {
        schoolId: this.model.school.id,
        resourceType: 2,
        resourceId: this.model.school.id,
        snapshotType: SnapshotType.ON_DEMAND,
      });
      this.displayedCustomSnapshotSets = newCustomSets;
      return;
    }
    // Default, most likely triggered from the logs for admins
    if (!isEmpty(this.selectedType)) {
      let newAutoSets = await this.store.query('snapshots/snapshot-set', {
        schoolId: this.model.school.id,
        resourceType: 2,
        resourceId: this.model.school.id,
        snapshotType: this.selectedType,
      });
      this.displayedAutoSnapshotSets = newAutoSets;
    }

    let newCustomSets = await this.store.query('snapshots/snapshot-set', {
      schoolId: this.model.school.id,
      resourceType: 2,
      resourceId: this.model.school.id,
      snapshotType: SnapshotType.ON_DEMAND,
    });
    this.displayedCustomSnapshotSets = newCustomSets;
  }

  @action
  handlePollingResult(mode) {
    this.refreshSnapshotSets(mode);
  }

  @action
  setProcessMonitorId(monitorId) {
    this.currentProcessMonitorId = monitorId;
  }

  @action
  copyToRegenerate(snapshotSet) {
    this.startDate = snapshotSet.startDate;
    this.endDate = snapshotSet.endDate;
    this.term = snapshotSet.term;
    this.year = snapshotSet.year;
    this.selectedType = snapshotSet.snapshotType;
  }
}
